import React from 'react';
import { TransitionGroup } from 'react-transition-group';
import { PlanSelect } from '../_components/PlanSelect';
import { ItemSelect } from '../_components/ItemSelect';
import { getScopeAccent } from '../_components/CouponScopeTag';
import { CouponScopeLabels } from '../../../../api/payments/coupons/domain/labels';
import { DiscountPricePreview } from '../_components/DiscountPricePreview';
import { useAddCouponBootstrap } from './add-coupon.bootstrap.hook';
import {
  CouponScopeEnum,
  CouponTypeEnum,
  CouponValueTypeEnum,
} from '../../../../api/payments/coupons/domain/enum';
import { formatCouponCode } from '../utils';
import { CycleRangeTimeline } from '../_components/CycleRangeTimeline';
import { SegmentedControl } from '../_components/SegmentedControl';
import {
  CouponRulesOverview,
  getRuleCardId,
} from '../_components/CouponRulesOverview';
import { JdsButton } from '../../../../components/jds/JdsButton';
import { JdsCollapse } from '../../../../components/jds/JdsCollapse';
import { JdsFormSkeleton } from '../../../../components/jds/JdsSkeleton';
import '../../../../components/jds/jds.css';

const ACTIVE_OPTIONS = [
  { value: true, label: 'Ativado' },
  { value: false, label: 'Desativado' },
];

export default function AddCouponV2(props) {
  const couponId = props.match.params.id;
  const {
    notFound,
    fetchingCoupon,
    formik,
    plans,
    plansLoading,
    extraPlansById,
    addRule,
    removeRule,
    handleRuleEntered,
    updateRule,
    duplicateRule,
    updateRuleScope,
    setRuleCycleRange,
    setRuleDesiredPrice,
    updateRulePlan,
    updateRuleItem,
    handleCancel,
    valueTypeOptions,
    appliesToOptions,
    typeOptions,
  } = useAddCouponBootstrap(couponId, props.location.state?.coupon);

  if (notFound) {
    return (
      <div className="jds-scope">
        <span>
          Cupom não encontrado. Volte para a listagem e tente novamente.
        </span>
      </div>
    );
  }

  if (fetchingCoupon) return <JdsFormSkeleton fields={7} />;

  // Erros das regras só depois da 1ª tentativa de salvar: evita mostrar
  // "Informe o produto" etc. enquanto o usuário ainda está preenchendo.
  const ruleError = (index, field) =>
    formik.submitCount > 0 ? formik.errors.rules?.[index]?.[field] : undefined;

  const formActions = (
    <div className="d-flex flex-md-row" style={{ gap: '8px' }}>
      <JdsButton variant="outlined" color="neutral" onClick={handleCancel}>
        Cancelar
      </JdsButton>
      <JdsButton type="submit" loading={formik.isSubmitting}>
        {formik.isSubmitting ? 'Salvando...' : 'Salvar'}
      </JdsButton>
    </div>
  );

  return (
    <>
      <form onSubmit={formik.handleSubmit} className="jds-scope">
        <div className="jds-card">
          <div className="mt-4">
            <div
              className="d-flex align-content-between justify-content-between"
              style={{ gap: '8px', marginBottom: 'var(--space-32)' }}
            >
              <h1 className="jds-title-lg">
                {couponId ? 'Editar cupom' : 'Criar cupom'}
              </h1>
              {formActions}
            </div>
            <div className="form-group row">
              <div className="col-lg-4">
                <label htmlFor="coupon-code">Nome do cupom</label>
                <input
                  id="coupon-code"
                  type="text"
                  className="form-control"
                  style={{ textTransform: 'uppercase' }}
                  value={formik.values.code}
                  onBlur={formik.handleBlur('code')}
                  onChange={(e) =>
                    formik.setFieldValue(
                      'code',
                      formatCouponCode(e.target.value),
                    )
                  }
                />
                <small className="text-muted">
                  Use apenas letras, números, &quot;_&quot; ou &quot;-&quot;
                  (sem espaços ou acentos) — o texto é convertido
                  automaticamente em maiúsculas.
                </small>
                <JdsCollapse in={!!(formik.touched.code && formik.errors.code)}>
                  <div className="text-danger">{formik.errors.code}</div>
                </JdsCollapse>
              </div>
              <div className="col-lg-4">
                <label>Tipo do cupom</label>
                <SegmentedControl
                  ariaLabel="Tipo do cupom"
                  options={typeOptions}
                  value={formik.values.type}
                  onChange={(value) => formik.setFieldValue('type', value)}
                />
                <JdsCollapse in={!!(formik.touched.type && formik.errors.type)}>
                  <div className="text-danger">{formik.errors.type}</div>
                </JdsCollapse>
              </div>
              <div className="col-lg-4 text-lg-right">
                <JdsCollapse
                  in={formik.values.type === CouponTypeEnum.ENROLLMENT}
                >
                  <>
                    <label>Duração do ciclo (dias)</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      {...formik.getFieldProps('cycleDays')}
                    />
                    <JdsCollapse
                      in={
                        !!(formik.touched.cycleDays && formik.errors.cycleDays)
                      }
                    >
                      <div className="text-danger">
                        {formik.errors.cycleDays}
                      </div>
                    </JdsCollapse>
                  </>
                </JdsCollapse>
              </div>
            </div>

            <JdsCollapse in={formik.values.type === CouponTypeEnum.ENROLLMENT}>
              <div className="jds-notice">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  style={{ flexShrink: 0, marginTop: 'var(--space-4)' }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 11v6M12 7.5v.5" />
                </svg>
                <div>
                  <strong>Cupom de Adesão:</strong> permite customizar a duração
                  do primeiro ciclo. A{' '}
                  <strong>"Duração do ciclo (dias)"</strong> define o tamanho{' '}
                  <strong>apenas do 1º ciclo</strong> — os ciclos seguintes
                  continuam com a duração do plano.
                </div>
              </div>
            </JdsCollapse>

            <div className="form-group row">
              <div className="col-lg-6">
                <label>Data de início</label>
                <input
                  type="date"
                  className="form-control"
                  {...formik.getFieldProps('startsAt')}
                />
                <JdsCollapse
                  in={!!(formik.touched.startsAt && formik.errors.startsAt)}
                >
                  <div className="text-danger">{formik.errors.startsAt}</div>
                </JdsCollapse>
              </div>
              <div className="col-lg-6">
                <label>Data de expiração</label>
                <input
                  type="date"
                  className="form-control"
                  {...formik.getFieldProps('expiresAt')}
                />
                <small className="text-muted">
                  Sugestão: 1 ano a partir de hoje — edite se quiser outro
                  prazo.
                </small>
                <JdsCollapse
                  in={!!(formik.touched.expiresAt && formik.errors.expiresAt)}
                >
                  <div className="text-danger">{formik.errors.expiresAt}</div>
                </JdsCollapse>
              </div>
            </div>

            <div className="form-group row">
              <div className="col-lg-6">
                <label>Ativo</label>
                <SegmentedControl
                  ariaLabel="Status do cupom"
                  options={ACTIVE_OPTIONS}
                  value={formik.values.active}
                  onChange={(value) => formik.setFieldValue('active', value)}
                />
              </div>
              <div className="col-lg-6" />
            </div>
          </div>
          <div className="mt-4">
            <h3 className="jds-title-sm mb-3">Descontos</h3>

            <TransitionGroup component={null}>
              {formik.values.rules.map((rule, index) => {
                const resolvedPlan =
                  rule.scope === CouponScopeEnum.PLAN
                    ? plans.find((plan) => plan.id === rule.planId) ??
                      extraPlansById[rule.planId]
                    : null;
                const resolvedPlanPrice = Number(
                  resolvedPlan?.price ??
                    resolvedPlan?.amount ??
                    resolvedPlan?.value,
                );
                const showPriceModeToggle =
                  rule.scope === CouponScopeEnum.PLAN &&
                  rule.valueType === CouponValueTypeEnum.FIXED &&
                  Number.isFinite(resolvedPlanPrice);
                // Exibição do "Preço final desejado": usa o texto bruto digitado
                // enquanto ele existir (permite ficar vazio ao apagar); só cai
                // pro valor derivado do desconto já salvo na 1ª vez que o modo
                // fica visível sem nada digitado ainda nesta sessão (ex.: editar
                // um cupom existente). Nunca escreve esse valor derivado de
                // volta no estado — é só exibição.
                const desiredPriceDisplay =
                  rule._desiredPriceText !== ''
                    ? rule._desiredPriceText
                    : rule.value !== '' && Number.isFinite(resolvedPlanPrice)
                    ? String(
                        Math.round(
                          (resolvedPlanPrice - Number(rule.value)) * 100,
                        ) / 100,
                      )
                    : '';
                const isNegativeClamped =
                  showPriceModeToggle &&
                  desiredPriceDisplay !== '' &&
                  Number.isFinite(Number(desiredPriceDisplay)) &&
                  Number(desiredPriceDisplay) > resolvedPlanPrice;

                return (
                  <JdsCollapse
                    key={rule._key}
                    onEntered={() => handleRuleEntered(rule)}
                  >
                    <div
                      id={getRuleCardId(rule)}
                      className={`jds-surface${
                        rule._flash ? ' jds-flash' : ''
                      }`}
                      style={{
                        borderLeft: `4px solid ${getScopeAccent(rule.scope)}`,
                        transition:
                          'border-color var(--dur-base) var(--ease-out)',
                      }}
                    >
                      <div
                        className="d-flex align-items-start"
                        style={{ gap: '12px' }}
                      >
                        <div style={{ flex: 1 }}>
                          <h4 className="jds-title-xs mb-3">
                            <span key={index} className="jds-fade">
                              Desconto {index + 1}
                            </span>
                          </h4>
                          <div
                            className="form-group row"
                            style={{ marginBottom: 'var(--space-16)' }}
                          >
                            <div className="col-lg-4">
                              <label>Aplicável em</label>
                              <SegmentedControl
                                ariaLabel="Aplicável em"
                                options={
                                  appliesToOptions.some(
                                    (opt) => opt.value === rule.scope,
                                  )
                                    ? appliesToOptions
                                    : [
                                        ...appliesToOptions,
                                        {
                                          // escopo não oferecido no seletor (ex.:
                                          // ITEM_TOTAL): segue visível ao editar
                                          // uma regra já salva com ele.
                                          value: rule.scope,
                                          label:
                                            CouponScopeLabels[rule.scope] ??
                                            rule.scope,
                                        },
                                      ]
                                }
                                value={rule.scope}
                                onChange={(value) =>
                                  updateRuleScope(index, value)
                                }
                              />
                            </div>
                          </div>

                          <JdsCollapse
                            in={
                              rule.scope === CouponScopeEnum.PLAN ||
                              rule.scope === CouponScopeEnum.ITEM
                            }
                          >
                            <div
                              className="form-group row"
                              style={{ marginBottom: 'var(--space-16)' }}
                            >
                              <div className="col-lg-8">
                                {rule.scope === CouponScopeEnum.PLAN && (
                                  <>
                                    <PlanSelect
                                      plans={plans}
                                      plansLoading={plansLoading}
                                      value={rule.planId}
                                      onChange={(id) =>
                                        updateRulePlan(index, id)
                                      }
                                      error={ruleError(index, 'planId')}
                                    />
                                    <DiscountPricePreview
                                      plan={resolvedPlan}
                                      valueType={rule.valueType}
                                      value={rule.value}
                                    />
                                  </>
                                )}
                                {rule.scope === CouponScopeEnum.ITEM && (
                                  <ItemSelect
                                    value={rule.itemId}
                                    valueType={rule.valueType}
                                    discountValue={rule.value}
                                    onChange={(id, item) =>
                                      updateRuleItem(index, id, item?.name)
                                    }
                                    error={ruleError(index, 'itemId')}
                                  />
                                )}
                              </div>
                            </div>
                          </JdsCollapse>

                          <div
                            className="form-group row"
                            style={{ marginBottom: 'var(--space-16)' }}
                          >
                            <div className="col-lg-4">
                              <label>Tipo</label>
                              <SegmentedControl
                                ariaLabel="Tipo do desconto"
                                options={valueTypeOptions}
                                value={rule.valueType}
                                onChange={(type) =>
                                  updateRule(index, 'valueType', type)
                                }
                              />
                              <JdsCollapse in={!!ruleError(index, 'valueType')}>
                                <div className="text-danger">
                                  {ruleError(index, 'valueType')}
                                </div>
                              </JdsCollapse>
                            </div>
                            <div
                              className={
                                showPriceModeToggle ? 'col-lg-8' : 'col-lg-4'
                              }
                            >
                              <label>Valor</label>
                              <JdsCollapse in={showPriceModeToggle}>
                                <SegmentedControl
                                  ariaLabel="Modo de preenchimento do valor"
                                  compact
                                  options={[
                                    {
                                      value: 'finalPrice',
                                      label: 'Preço final desejado',
                                    },
                                    {
                                      value: 'discount',
                                      label: 'Valor do desconto',
                                    },
                                  ]}
                                  value={rule._priceInputMode}
                                  onChange={(mode) =>
                                    updateRule(index, '_priceInputMode', mode)
                                  }
                                />
                              </JdsCollapse>
                              {showPriceModeToggle &&
                              rule._priceInputMode === 'finalPrice' ? (
                                <>
                                  <div
                                    className="input-group"
                                    style={{
                                      marginTop: 'var(--space-8)',
                                      maxWidth: 280,
                                    }}
                                  >
                                    <div className="input-group-prepend">
                                      <span className="input-group-text">
                                        R$
                                      </span>
                                    </div>
                                    <input
                                      type="number"
                                      step="any"
                                      min="0"
                                      className="form-control"
                                      value={desiredPriceDisplay}
                                      onChange={(e) => {
                                        const typed = e.target.value;
                                        const parsed = Number(typed);
                                        const newValue =
                                          typed === ''
                                            ? ''
                                            : Number.isFinite(parsed)
                                            ? Math.round(
                                                Math.max(
                                                  0,
                                                  resolvedPlanPrice - parsed,
                                                ) * 100,
                                              ) / 100
                                            : rule.value;
                                        setRuleDesiredPrice(
                                          index,
                                          typed,
                                          newValue,
                                        );
                                      }}
                                    />
                                  </div>
                                  <JdsCollapse in={isNegativeClamped}>
                                    <small className="text-danger d-block">
                                      O preço desejado é maior que o preço atual
                                      do plano — desconto ajustado para R$0,00.
                                    </small>
                                  </JdsCollapse>
                                </>
                              ) : (
                                <div
                                  className="input-group"
                                  style={{
                                    marginTop: showPriceModeToggle
                                      ? 'var(--space-8)'
                                      : 0,
                                    maxWidth: showPriceModeToggle
                                      ? 280
                                      : undefined,
                                  }}
                                >
                                  {rule.valueType ===
                                    CouponValueTypeEnum.FIXED && (
                                    <div className="input-group-prepend">
                                      <span className="input-group-text">
                                        R$
                                      </span>
                                    </div>
                                  )}
                                  <input
                                    type="number"
                                    step="any"
                                    min="0"
                                    max={
                                      rule.valueType ===
                                      CouponValueTypeEnum.PERCENT
                                        ? 100
                                        : undefined
                                    }
                                    className="form-control"
                                    value={rule.value}
                                    onChange={(e) =>
                                      // limpa _desiredPriceText junto: evita que o
                                      // texto digitado antes no modo "Preço final
                                      // desejado" fique desatualizado e reapareça
                                      // quando o modo for alternado de volta.
                                      setRuleDesiredPrice(
                                        index,
                                        '',
                                        e.target.value,
                                      )
                                    }
                                  />
                                  {rule.valueType ===
                                    CouponValueTypeEnum.PERCENT && (
                                    <div className="input-group-append">
                                      <span className="input-group-text">
                                        %
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                              <JdsCollapse in={!!ruleError(index, 'value')}>
                                <div className="text-danger">
                                  {ruleError(index, 'value')}
                                </div>
                              </JdsCollapse>
                            </div>
                          </div>

                          <div
                            className="form-group row"
                            style={{ marginBottom: 'var(--space-16)' }}
                          >
                            <div className="col-lg-8">
                              <label>Ciclos em que o desconto se aplica</label>
                              <CycleRangeTimeline
                                fromCycle={rule.fromCycle}
                                toCycle={rule.toCycle}
                                onChange={(from, to) =>
                                  setRuleCycleRange(index, from, to)
                                }
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-8)',
                            marginTop: 'var(--space-24)',
                          }}
                        >
                          <JdsButton
                            variant="ghost"
                            iconOnly
                            title="Duplicar desconto"
                            aria-label="Duplicar desconto"
                            onClick={() => duplicateRule(index)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <rect x="9" y="9" width="12" height="12" rx="2" />
                              <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                            </svg>
                          </JdsButton>
                          <JdsButton
                            variant="ghost"
                            color="error"
                            iconOnly
                            disabled={index === 0}
                            title="Remover desconto"
                            aria-label="Remover desconto"
                            onClick={() => removeRule(index)}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v5M14 11v5" />
                            </svg>
                          </JdsButton>
                        </div>
                      </div>
                    </div>
                  </JdsCollapse>
                );
              })}
            </TransitionGroup>
            <JdsButton variant="outlined" onClick={addRule}>
              Adicionar desconto
            </JdsButton>
            <CouponRulesOverview
              rules={formik.values.rules}
              plans={plans}
              extraPlansById={extraPlansById}
            />
          </div>
          <div className="mt-4">
            <h3 className="jds-title-sm mb-3">Observação</h3>
            <textarea
              className="form-control"
              rows={4}
              {...formik.getFieldProps('description')}
            />
          </div>
          <div
            className="d-flex justify-content-end"
            style={{ marginTop: 'var(--space-24)' }}
          >
            {formActions}
          </div>
        </div>
      </form>
    </>
  );
}
